import { useState, useRef, useCallback } from 'react';

export const useAutoCapitalize = (initialValue = '') => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Handle input changes with auto-capitalization
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value: newValue, selectionStart } = e.target;
    
    // If the input is empty, just set it
    if (!newValue) {
      setValue('');
      return;
    }
    
    // Capitalize first letter of the input
    if (newValue.length === 1) {
      setValue(newValue.toUpperCase());
      return;
    }
    
    // For existing text, check if we're at the start of a new sentence
    const cursorPos = selectionStart || 0;
    if (cursorPos >= 2 && 
        '.!?'.includes(newValue[cursorPos - 2]) && 
        newValue[cursorPos - 1] === ' ' &&
        cursorPos < newValue.length &&
        newValue[cursorPos] === newValue[cursorPos].toLowerCase()) {
      
      const beforeCursor = newValue.substring(0, cursorPos);
      const afterCursor = newValue.substring(cursorPos);
      const updatedValue = beforeCursor + afterCursor.charAt(0).toUpperCase() + afterCursor.slice(1);
      
      setValue(updatedValue);
      
      // Move cursor to maintain position after state update
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.selectionStart = cursorPos + 1;
          inputRef.current.selectionEnd = cursorPos + 1;
        }
      }, 0);
      return;
    }
    
    setValue(newValue);
  }, []);

  // Handle space key press for sentence capitalization and Enter key for form submission
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Handle Enter key: submit form unless Shift is held
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Trigger form submission by finding and clicking the submit button
      const form = inputRef.current?.closest('form');
      if (form) {
        form.requestSubmit();
      }
      return;
    }
    
    // Auto-capitalize after pressing space after a sentence end
    if (e.key === ' ' && inputRef.current) {
      const cursorPos = inputRef.current.selectionStart || 0;
      const textBeforeCursor = inputRef.current.value.substring(0, cursorPos);
      
      // Check if we're at the end of a sentence
      if (cursorPos >= 2 && 
          '.!?'.includes(textBeforeCursor[cursorPos - 2]) && 
          textBeforeCursor[cursorPos - 1] === ' ') {
        
        // Let the space be added first, then we'll handle the capitalization
        setTimeout(() => {
          if (inputRef.current) {
            const currentValue = inputRef.current.value;
            if (cursorPos < currentValue.length) {
              const newValue = 
                currentValue.substring(0, cursorPos + 1) + 
                currentValue[cursorPos + 1].toUpperCase() + 
                currentValue.substring(cursorPos + 2);
              
              setValue(newValue);
              
              // Move cursor to maintain position
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.selectionStart = cursorPos + 2;
                  inputRef.current.selectionEnd = cursorPos + 2;
                }
              }, 0);
            }
          }
        }, 0);
      }
    }
  }, []);

  return {
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    ref: inputRef,
    setValue,
  };
};
